import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2 } from 'lucide-react';
import type { Book } from '../../../shared/types/index.js';
import { slugify } from '../../../shared/utils/stringUtils.js';
import {
  BadgeHeader,
  BadgeCustomizerControls,
  BadgeLivePreview,
  BadgeCodeSnippets,
  BadgeFooter,
} from './embed-badge/index.js';
import { useBodyScrollLock } from '../../../shared/utils/scrollLock.js';

interface EmbedBadgeModalProps {
  book: Book;
  isOpen: boolean;
  onClose: () => void;
  onOpenVerifyModal?: (uibn: string) => void;
}

export const EmbedBadgeModal: React.FC<EmbedBadgeModalProps> = ({
  book,
  isOpen,
  onClose,
  onOpenVerifyModal,
}) => {
  const [badgeStyle, setBadgeStyle] = useState<'pill' | 'card' | 'shield' | 'button'>('pill');
  const [theme, setTheme] = useState<'emerald' | 'light' | 'dark' | 'indigo'>('emerald');
  const [activeCodeTab, setActiveCodeTab] = useState<'script' | 'iframe' | 'svg' | 'url'>('script');
  const [copied, setCopied] = useState(false);
  const [previewContext, setPreviewContext] = useState<'isolated' | 'mockup'>('isolated');

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !book) return null;

  // Base URL calculation
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://uibn.id';
  const cleanUibn = book.uibn;

  const slug = slugify(book.title);
  const verifyUrl = `${origin}/buku/${slug}`;
  const svgBadgeUrl = `${origin}/api/badge/${encodeURIComponent(cleanUibn)}.svg?style=${badgeStyle === 'button' ? 'pill' : badgeStyle}&theme=${theme}`;
  const iframeUrl = `${origin}/embed/badge/${encodeURIComponent(cleanUibn)}?style=${badgeStyle}&theme=${theme}${badgeStyle === 'pill' || badgeStyle === 'shield' ? '&compact=true' : ''}`;
  const scriptUrl = `${origin}/api/badge/widget.js`;

  // Code generator snippets
  const getEmbedCode = () => {
    switch (activeCodeTab) {
      case 'script':
        return `<!-- Widget Verifikasi Resmi UIBN -->
<div class="uibn-badge" 
     data-uibn="${cleanUibn}" 
     data-style="${badgeStyle === 'button' ? 'pill' : badgeStyle}" 
     data-theme="${theme}">
  <a href="${verifyUrl}" target="_blank" rel="noopener noreferrer">
    Buku ini terdaftar resmi di UIBN (${cleanUibn})
  </a>
</div>
<script src="${scriptUrl}" async></script>`;

      case 'iframe': {
        const height = badgeStyle === 'card' ? '120' : badgeStyle === 'shield' ? '36' : '44';
        const width = badgeStyle === 'card' ? '340' : badgeStyle === 'shield' ? '230' : '380';
        return `<!-- Iframe Lencana Verifikasi UIBN -->
<iframe src="${iframeUrl}" 
        width="${width}" 
        height="${height}" 
        frameborder="0" 
        scrolling="no" 
        style="border: none; overflow: hidden; max-width: 100%;" 
        title="Status Verifikasi UIBN: ${book.title}">
</iframe>`;
      }

      case 'svg':
        return `<!-- Gambar Lencana SVG Interaktif UIBN (Bisa untuk Markdown / HTML) -->
<a href="${verifyUrl}" target="_blank" rel="noopener noreferrer">
  <img src="${svgBadgeUrl}" alt="Buku ini terdaftar resmi di UIBN - ${cleanUibn}" />
</a>

<!-- Versi Markdown (Untuk README / Blog): -->
[![Buku ini terdaftar resmi di UIBN](${svgBadgeUrl})](${verifyUrl})`;

      case 'url':
        return verifyUrl;

      default:
        return '';
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getEmbedCode());
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/80 backdrop-blur-xs p-2 sm:p-4 overflow-y-auto overscroll-contain animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] sm:max-h-[88vh] my-auto overflow-hidden shadow-2xl border border-slate-200 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 1. Modal Header */}
        <BadgeHeader onClose={onClose} />

        {/* Modal Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-5 flex-1 bg-slate-50/40">
          {/* Top Info Banner */}
          <div className="p-3 bg-emerald-50/90 border border-emerald-200/80 rounded-xl flex items-start gap-2.5 text-xs text-emerald-900 shadow-2xs">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold flex items-center gap-1.5">
                <span>Pernyataan Otentisitas:</span>
                <span className="font-semibold text-emerald-950">"Buku ini terdaftar resmi di UIBN"</span>
              </div>
              <p className="text-[11px] text-emerald-800/90 mt-0.5">
                Ketika calon pembaca atau mitra mengeklik widget ini di situs web Anda, mereka akan langsung diarahkan ke halaman status verifikasi resmi buku <strong>{book.title}</strong> (UIBN: {book.uibn}).
              </p>
            </div>
          </div>

          {/* 2. Style and Theme Customizer Controls */}
          <BadgeCustomizerControls
            badgeStyle={badgeStyle}
            theme={theme}
            onBadgeStyleChange={setBadgeStyle}
            onThemeChange={setTheme}
          />

          {/* 3. Interactive Live Preview */}
          <BadgeLivePreview
            book={book}
            badgeStyle={badgeStyle}
            theme={theme}
            previewContext={previewContext}
            verifyUrl={verifyUrl}
            onPreviewContextChange={setPreviewContext}
            onOpenVerifyModal={onOpenVerifyModal}
          />

          {/* 4. Snippet Code Tabs & Copy */}
          <BadgeCodeSnippets
            activeCodeTab={activeCodeTab}
            embedCode={getEmbedCode()}
            copied={copied}
            onCodeTabChange={setActiveCodeTab}
            onCopy={handleCopy}
          />
        </div>

        {/* 5. Modal Footer */}
        <BadgeFooter
          cleanUibn={cleanUibn}
          copied={copied}
          onCopy={handleCopy}
          onClose={onClose}
        />
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
